/**
 * CasePage — the one live page per case that the specialist keeps current (work package / report):
 * text, key readings, charts, tables, ranked suspects, safety, drafts, evidence and task cards,
 * plus the comment thread with @mentions of specialists. "Customer view" drops internal blocks and
 * the thread; Share creates a view-only customer link.
 */

import { useMemo, useRef, useState } from 'react';
import {
  X, Share2, TriangleAlert, FileText, AtSign, Send, Copy, Eye, ClipboardList,
} from 'lucide-react';
import SyntheticBadge from './SyntheticBadge.jsx';
import SpecialistDot from './SpecialistDot.jsx';
import { clockOf } from './agentUi.js';
import './CasePage.css';

/** Generic suspect role badge; the vertical passes its own palette as `suspectRole`. */
const plainRole = (role) => {
  const r = String(role || 'context');
  return { color: 'var(--text-2)', icon: '·', label: r.charAt(0).toUpperCase() + r.slice(1) };
};

const DRAFT_STATUS = {
  draft: 'Draft', pending_approval: 'Waiting for approval', approved: 'Approved', submitted: 'Submitted',
};

function Block({ block, fileUrl, onOpenCitation, suspectRole = plainRole }) {
  switch (block.kind) {
    case 'text':
      return <p className="cpage__text">{block.body}</p>;
    case 'facts':
      return (
        <dl className="cpage__facts">
          {(block.items || []).map((f) => (
            <div key={f.label} className="cpage__fact">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
              {f.note && <span className="cpage__note">{f.note}</span>}
            </div>
          ))}
        </dl>
      );
    case 'chart':
      return (
        <figure className="cpage__chart">
          {fileUrl && block.path ? <img src={fileUrl(block.path)} alt={block.title || block.path} /> : <div className="cpage__note">{block.path}</div>}
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    case 'table':
      return (
        <div className="cpage__tablewrap">
          <table className="cpage__table">
            <thead><tr>{(block.columns || []).map((c) => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {(block.rows || []).map((r, i) => (
                <tr key={`${i}-${r[0]}`}>{r.map((cell, j) => <td key={`${j}-${cell}`}>{String(cell ?? '')}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'suspects':
      return (
        <ol className="cpage__suspects">
          {(block.items || []).map((s) => {
            const role = suspectRole(s.role);
            return (
              <li key={`${s.rank}-${s.cad_id}`}>
                <span className="cpage__rank">{s.rank}</span>
                <button type="button" className="cpage__part" onClick={() => onOpenCitation?.({ cad_id: s.cad_id })} disabled={!onOpenCitation} title={s.cad_id}>
                  {s.name || s.cad_id}
                </button>
                <span className="cpage__role" style={{ color: role.color }}>
                  <span aria-hidden="true">{role.icon}</span> {role.label}
                </span>
                {s.rationale && <span className="cpage__rationale">{s.rationale}</span>}
              </li>
            );
          })}
        </ol>
      );
    case 'safety':
      return (
        <ul className="cpage__safety">
          {(block.lines || []).map((l) => <li key={l}><TriangleAlert size={12} aria-hidden="true" /> {l}</li>)}
        </ul>
      );
    case 'drafts':
      return (
        <ul className="cpage__drafts">
          {(block.items || []).map((d) => (
            <li key={d.draft_id}><FileText size={12} aria-hidden="true" /> {d.title} <span className="cpage__note">{DRAFT_STATUS[d.status] ?? d.status}</span></li>
          ))}
        </ul>
      );
    case 'evidence':
      return (
        <ul className="cpage__evidence">
          {(block.items || []).map((e) => <li key={`${e.kind}-${e.ref}`}><span className="cpage__kind">{e.kind}</span> {e.label} <span className="cpage__note">{e.ref}</span></li>)}
        </ul>
      );
    case 'task_cards':
      return (
        <div className="cpage__tasks">
          {(block.items || []).map((t) => (
            <details key={t.task_id} className="cpage__task">
              <summary><ClipboardList size={12} aria-hidden="true" /> <span className="cpage__kind">{t.task_id}</span> {t.title}</summary>
              {(t.safety || []).length > 0 && (
                <ul className="cpage__safety">{t.safety.map((l) => <li key={l}><TriangleAlert size={12} aria-hidden="true" /> {l}</li>)}</ul>
              )}
              {(t.tools || []).length > 0 && <p className="cpage__note">Tools: {t.tools.join(', ')}</p>}
              {(t.access_parts || []).length > 0 && <p className="cpage__note">Access: {t.access_parts.join(', ')}</p>}
              {(t.steps || []).length > 0 && <ol className="cpage__steps">{t.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
            </details>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function MentionInput({ specialists, onSubmit }) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState(null);
  const ref = useRef(null);
  const list = useMemo(() => Object.values(specialists || {}), [specialists]);
  const matches = query === null ? [] : list.filter((s) => {
    const q = query.toLowerCase();
    return s.name?.toLowerCase().startsWith(q) || s.id?.toLowerCase().startsWith(q);
  });

  const update = (value, caret) => {
    setText(value);
    const m = /@(\w*)$/.exec(value.slice(0, caret));
    setQuery(m ? m[1] : null);
  };

  const pick = (s) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@(\w*)$/, `@${s.name} `);
    const next = before + text.slice(caret);
    setText(next);
    setQuery(null);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); });
  };

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onSubmit?.(t);
    setText('');
    setQuery(null);
  };

  return (
    <form className="cpage__compose" onSubmit={submit}>
      {matches.length > 0 && (
        <ul className="cpage__mentions" role="listbox" aria-label="Mention a specialist">
          {matches.map((s) => (
            <li key={s.id}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(s); }}>
                <SpecialistDot specialist={s} showName /> <span className="cpage__note">{s.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <AtSign size={13} aria-hidden="true" className="cpage__at" />
      <input
        ref={ref}
        type="text"
        value={text}
        maxLength={2000}
        placeholder="Comment, or @mention a specialist"
        aria-label="Comment"
        onChange={(e) => update(e.target.value, e.target.selectionStart ?? e.target.value.length)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && matches.length > 0) { e.preventDefault(); pick(matches[0]); }
          if (e.key === 'Escape' && query !== null) { e.stopPropagation(); setQuery(null); }
        }}
      />
      <button type="submit" className="cpage__send" disabled={!text.trim()} aria-label="Post comment"><Send size={13} aria-hidden="true" /></button>
    </form>
  );
}

export default function CasePage({
  page = null, comments = [], specialists = {}, onComment, onShare, view = 'full', onViewChange,
  fileUrl = null, onClose, onOpenCitation,
  /** (role) -> {color, icon, label} for ranked suspects; a plain badge by default. */
  suspectRole = plainRole,
}) {
  const [share, setShare] = useState({ phase: 'idle', url: null, error: null });
  const customer = view === 'customer';
  const blocks = (page?.blocks || []).filter((b) => !(customer && b.internal));

  const doShare = async () => {
    if (typeof onShare !== 'function') return;
    setShare({ phase: 'loading', url: null, error: null });
    try {
      const r = await onShare();
      setShare({ phase: 'ready', url: r?.url ?? null, error: null });
    } catch (err) {
      setShare({ phase: 'error', url: null, error: err.message });
    }
  };

  return (
    <section className="agent-panel cpage" aria-label="Case page">
      <header className="agent-panel__head">
        <div className="agent-panel__titles">
          <span className="agent-panel__kicker">Case page{page ? ` · v${page.version}` : ''}</span>
          <h2 className="agent-panel__title">{page?.title ?? 'No page yet'}</h2>
        </div>
        <div className="cpage__tools">
          {typeof onViewChange === 'function' && (
            <button type="button" className={`agent-btn${customer ? ' is-on' : ''}`} aria-pressed={customer} onClick={() => onViewChange(customer ? 'full' : 'customer')}>
              <Eye size={12} aria-hidden="true" /> Customer view
            </button>
          )}
          {typeof onShare === 'function' && (
            <button type="button" className="agent-btn" onClick={doShare} disabled={share.phase === 'loading'}>
              <Share2 size={12} aria-hidden="true" /> Share
            </button>
          )}
          {onClose && <button type="button" className="agent-icon" onClick={onClose} aria-label="Close the case page"><X size={14} aria-hidden="true" /></button>}
        </div>
      </header>

      {share.phase === 'ready' && share.url && (
        <div className="cpage__share">
          <span>Customer link (view only, no case log or memory):</span>
          <a href={share.url} target="_blank" rel="noreferrer">{share.url}</a>
          <button type="button" className="agent-icon" aria-label="Copy link" onClick={() => navigator.clipboard?.writeText(share.url).catch(() => {})}>
            <Copy size={12} aria-hidden="true" />
          </button>
        </div>
      )}
      {share.phase === 'error' && <div className="cpage__share is-error">Could not create a link: {share.error}</div>}

      <div className="agent-panel__body">
        {blocks.length === 0 && <p className="cpage__note">The specialist has not written anything here yet.</p>}
        {blocks.map((b) => (
          <section key={b.id} className={`cpage__block is-${b.kind}${b.internal ? ' is-internal' : ''}`}>
            {b.title && <h3 className="cpage__btitle">{b.title}{b.internal && <span className="cpage__internal">Internal</span>}</h3>}
            <Block block={b} fileUrl={fileUrl} onOpenCitation={onOpenCitation} suspectRole={suspectRole} />
          </section>
        ))}

        {!customer && (
          <section className="cpage__thread" aria-label="Comments">
            <h3 className="cpage__btitle">Comments</h3>
            {comments.length === 0 && <p className="cpage__note">No comments yet.</p>}
            <ul className="cpage__comments">
              {comments.map((c) => {
                const spec = c.author?.kind === 'specialist' ? specialists[c.author.id] : null;
                return (
                  <li key={c.id} className={c.parent_id ? 'is-reply' : undefined}>
                    <div className="cpage__chead">
                      {spec ? <SpecialistDot specialist={spec} showName /> : <strong>{c.author?.name ?? 'You'}</strong>}
                      <span className="cpage__note">{clockOf(c.created_at)}</span>
                    </div>
                    <p className="cpage__cbody">{c.body}</p>
                  </li>
                );
              })}
            </ul>
            {typeof onComment === 'function' && <MentionInput specialists={specialists} onSubmit={onComment} />}
          </section>
        )}
      </div>

      <footer className="agent-panel__foot"><SyntheticBadge /></footer>
    </section>
  );
}
